import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../App';
import { Loader2, Target, Pencil, Save, X, Sparkles, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAY_LABEL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DISMISS_PREFIX = 'rxgrit_intention_dismissed_';

const WeeklyIntentionCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(true); // small chip by default once dismissed/intention set

  const fetchIntention = useCallback(async () => {
    try {
      const res = await apiClient.get('/weekly-intention/current');
      setData(res.data);
      // Check per-week dismissal state
      const weekKey = res.data?.week_start ? `${DISMISS_PREFIX}${res.data.week_start}` : null;
      const wasDismissed = weekKey ? localStorage.getItem(weekKey) === '1' : false;
      setDismissed(wasDismissed);
      // Auto-open the editor only when nothing is set yet and user hasn't dismissed
      if (!res.data?.intention && !wasDismissed) {
        setEditing(true);
        setCollapsed(false);
        setDraft('');
      }
    } catch (err) {
      console.error('Failed to fetch weekly intention:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntention();
  }, [fetchIntention]);

  const dismissForWeek = () => {
    if (data?.week_start) {
      try { localStorage.setItem(`${DISMISS_PREFIX}${data.week_start}`, '1'); } catch (_e) { /* no-op */ }
    }
    setDismissed(true);
    setEditing(false);
  };

  const save = async () => {
    const text = draft.trim();
    if (!text) {
      toast.error('Give your intention a few words.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.post('/weekly-intention', { intention: text });
      setData({ ...data, intention: res.data });
      setEditing(false);
      setCollapsed(true);
      toast.success('Weekly intention set. Go make it happen.');
    } catch (err) {
      toast.error('Could not save your intention');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    setDraft(data?.intention?.intention || '');
    setEditing(true);
    setCollapsed(false);
  };

  if (loading) return null;
  if (!data) return null;
  if (dismissed) return null;

  const dow = data.day_of_week;
  const isReflection = data.is_reflection_phase;
  const hasIntention = !!data.intention;
  const dayLabel = WEEKDAY_LABEL[dow] || 'Today';

  const goalPrompt =
    dow === 0
      ? "It's Monday — what's your one non-negotiable this week?"
      : dow < 4
      ? `It's ${dayLabel} — still time. What's your one non-negotiable this week?`
      : "The week is almost over — but it's not too late. Name your one non-negotiable for the rest of the week.";

  const reflectionPrompt =
    dow === 4
      ? "It's Friday — how's this week's intention going?"
      : dow === 5
      ? "Saturday check-in — you're crushing this week's intention, right?"
      : "Sunday reset — one honest look at this week's intention before Monday resets everything.";

  // Collapsed chip state — small floating pill showing status, click to expand
  if (hasIntention && collapsed && !editing) {
    return (
      <div
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 animate-fade-in"
        data-testid="weekly-intention-chip-wrap"
      >
        <div className="flex items-center gap-2 bg-white border border-[#EFB342]/50 shadow-lg rounded-full pl-3 pr-1.5 py-1.5 max-w-[min(90vw,340px)]">
          <Sparkles className="text-[#EFB342] shrink-0" size={16} />
          <span className="text-xs text-stone-800 truncate flex-1" title={data.intention.intention}>
            &ldquo;{data.intention.intention}&rdquo;
          </span>
          <button
            type="button"
            onClick={startEdit}
            className="shrink-0 text-stone-500 hover:text-[#EFB342] p-1 rounded-full hover:bg-[#EFB342]/10 transition-colors"
            title="Edit this week's intention"
            data-testid="weekly-intention-chip-edit"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={dismissForWeek}
            className="shrink-0 text-stone-400 hover:text-stone-700 p-1 rounded-full hover:bg-stone-100 transition-colors"
            title="Hide until next week"
            data-testid="weekly-intention-chip-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Expanded popover — anchored to bottom-right, dismissible
  return (
    <div
      className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 w-[min(92vw,360px)] animate-fade-in"
      data-testid="weekly-intention-card"
    >
      <div className="relative rounded-2xl border border-[#EFB342]/40 bg-[#FFF8E7] shadow-xl p-4 sm:p-5">
        <button
          type="button"
          onClick={dismissForWeek}
          className="absolute top-2.5 right-2.5 text-stone-400 hover:text-stone-700 p-1 rounded-full hover:bg-stone-100 transition-colors"
          title="Hide until next week"
          data-testid="weekly-intention-close-btn"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="w-9 h-9 shrink-0 rounded-xl bg-[#EFB342]/20 border border-[#EFB342]/40 flex items-center justify-center">
            {isReflection && hasIntention ? (
              <Sparkles className="text-[#EFB342]" size={18} />
            ) : (
              <Target className="text-[#EFB342]" size={18} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-[#B8871E] font-semibold mb-0.5">
              {isReflection && hasIntention ? 'This Week\u2019s Intention' : 'Set Your Weekly Intention'}
            </p>
            <p className="text-xs text-stone-700 leading-snug">
              {isReflection && hasIntention ? reflectionPrompt : goalPrompt}
            </p>
          </div>
        </div>

        {editing ? (
          <div className="mt-3 space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 280))}
              rows={2}
              maxLength={280}
              autoFocus
              placeholder="e.g. Log every action every day. No excuses."
              className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder:text-stone-500 focus:outline-none focus:border-[#EFB342]/60 resize-none"
              data-testid="weekly-intention-textarea"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-stone-500">{draft.length}/280</span>
              <div className="flex items-center gap-2">
                {hasIntention && (
                  <button
                    type="button"
                    onClick={() => { setDraft(data?.intention?.intention || ''); setEditing(false); setCollapsed(true); }}
                    className="text-stone-600 hover:text-stone-900 text-xs px-2.5 py-1.5 rounded-lg"
                    data-testid="weekly-intention-cancel-btn"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !draft.trim()}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30] disabled:opacity-60 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                  data-testid="weekly-intention-save-btn"
                >
                  {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                  {hasIntention ? 'Update' : 'Set Intention'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          hasIntention && (
            <div className="mt-3 flex items-start gap-2">
              <p
                className="flex-1 text-stone-900 text-sm font-medium leading-snug"
                data-testid="weekly-intention-text"
              >
                &ldquo;{data.intention.intention}&rdquo;
              </p>
              <button
                type="button"
                onClick={startEdit}
                title="Edit this week's intention"
                className="shrink-0 text-stone-600 hover:text-[#EFB342] p-1.5 rounded-lg hover:bg-[#EFB342]/10 transition-colors"
                data-testid="weekly-intention-edit-btn"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                title="Minimize"
                className="shrink-0 text-stone-500 hover:text-stone-700 p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
              >
                <ChevronUp size={14} />
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default WeeklyIntentionCard;
